'use client';

import { useCallback } from 'react';
import { useFormatter, useTranslations } from 'next-intl';
import {
  Badge,
  Button,
  MarkdownPreview,
  Scroll,
  Spinner,
  Text,
  toasterApi,
} from '@terra/ui-design-system';
import {
  SkillsApiError,
  useSkill,
  useSkillAudit,
  useSkillsMutations,
  useSkillVersions,
} from '@terra/ui-skills';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { ArrowLeft02Icon, SquareLock01Icon } from '@terra/ui-icons';
import type { Tab } from '../../store/layoutStore';
import { useMainContentTabs } from '../../store/layoutStore';
import { useAppTargetId } from '../../store/projectStore';
import {
  EMPTY_SKILL_DRAFT,
  useSkillDraftsStore,
} from '../../store/skillDraftsStore';
import { useChatTabs } from '../../store/chatTabsStore';
import { useCreateNewChatTab } from '../chat/actions/useCreateNewChatTab';
import { createSkillMention } from '../../utils/skillMentionUtils';
import { getDraftFromSkill } from '../../utils/skillDraftUtils';
import {
  useSkillsAuditLabels,
  useSkillsDetailLabels,
  useSkillsEditorLabels,
  useSkillsListLabels,
  useSkillsVersionLabels,
} from '../../hooks/useSkillLabels';

type SkillTab = Extract<
  Tab,
  {
    type: 'skill';
  }
>;

export function SkillDetailContent({ tab }: { tab: SkillTab }) {
  const { skillId } = tab.data;
  const appTargetId = useAppTargetId();
  const tEditor = useTranslations('customSkills.editor');
  const formatter = useFormatter();
  const { tabs, openTab, setActiveTab, closeTab } = useMainContentTabs();
  const { chatTabs, setActiveChatTabId, updateChatTab } = useChatTabs();
  const { createNewChatTab } = useCreateNewChatTab();
  const setDraft = useSkillDraftsStore((state) => state.setDraft);
  const { skill, isLoading } = useSkill({
    skillId,
    applicationId: appTargetId,
  });
  const { versions } = useSkillVersions({
    skillId,
    applicationId: appTargetId,
  });
  const { entries } = useSkillAudit({ skillId, applicationId: appTargetId });
  const { publishSkill, rollbackSkill, deleteSkill, restoreSkill } =
    useSkillsMutations({ applicationId: appTargetId });
  const listLabels = useSkillsListLabels();
  const editorLabels = useSkillsEditorLabels();
  const versionLabels = useSkillsVersionLabels();
  const auditLabels = useSkillsAuditLabels();
  const detailLabels = useSkillsDetailLabels();
  const formatDate = useCallback(
    (date: Date) =>
      formatter.dateTime(date, { dateStyle: 'medium', timeStyle: 'short' }),
    [formatter],
  );
  const handleReturnToSkillsPage = useCallback(() => {
    openTab({
      id: 'skills-list',
      title: listLabels.title,
      type: 'skills-list',
    });
    closeTab(tab.id);
  }, [closeTab, listLabels.title, openTab, tab.id]);
  const isCustomer = skill?.source === 'CUSTOMER';
  const headerVersion = skill?.draftVersion ?? skill?.publishedVersion ?? null;
  const headerName =
    headerVersion?.frontmatter.name ?? detailLabels.untitledSkill;
  const headerDescription =
    skill?.publishedVersion?.frontmatter.description ??
    skill?.draftVersion?.frontmatter.description ??
    '';
  const bodyMarkdown =
    skill?.draftVersion?.body ?? skill?.publishedVersion?.body ?? '';
  const handleOpenSkillEditor = useCallback(() => {
    const draft = skill ? getDraftFromSkill(skill) : EMPTY_SKILL_DRAFT;
    const skillDraftId = `edit-${skillId}`;
    const existingEditorTab = tabs.find(
      (existingTab) =>
        existingTab.type === 'skill-editor' &&
        existingTab.data.skillDraftId === skillDraftId,
    );

    if (existingEditorTab) {
      setActiveTab(existingEditorTab.id);
    } else {
      setDraft(skillDraftId, draft);
      openTab({
        id: `skill-editor-${skillDraftId}`,
        title: draft.name || editorLabels.editTitle,
        type: 'skill-editor',
        data: { skillDraftId, mode: 'edit', skillId },
      });
    }

    const existingChatTab = chatTabs.find(
      (chatTab) => chatTab.skillDraftId === skillDraftId,
    );
    const skillMention = createSkillMention({
      skillDraftId,
      skillId,
      mode: 'edit',
      label: draft.name || draft.slug,
      fallbackLabel: editorLabels.editTitle,
    });

    if (existingChatTab) {
      updateChatTab(existingChatTab.tabId, { mentions: [skillMention] });
      setActiveChatTabId(existingChatTab.tabId);

      return;
    }

    createNewChatTab({
      skillDraftId,
      initialDraftInput: tEditor('seedEditChat'),
      initialMentions: [skillMention],
    });
  }, [
    chatTabs,
    createNewChatTab,
    editorLabels.editTitle,
    openTab,
    setActiveChatTabId,
    setActiveTab,
    setDraft,
    skill,
    skillId,
    tabs,
    tEditor,
    updateChatTab,
  ]);
  const handlePublish = async () => {
    try {
      await publishSkill.mutateAsync({ skillId });
      toasterApi.success({
        title: editorLabels.successTitle,
        description: editorLabels.publishedToast,
      });
    } catch {
      toasterApi.error({
        title: editorLabels.errorTitle,
        description: editorLabels.error,
      });
    }
  };

  const handleRollback = async (versionId: string) => {
    try {
      await rollbackSkill.mutateAsync({ skillId, body: { versionId } });
      toasterApi.success({
        title: editorLabels.successTitle,
        description: editorLabels.rolledBackToast,
      });
    } catch {
      toasterApi.error({
        title: editorLabels.errorTitle,
        description: editorLabels.error,
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteSkill.mutateAsync(skillId);
      toasterApi.success({
        title: editorLabels.successTitle,
        description: editorLabels.deletedToast,
      });
      closeTab(tab.id);
    } catch (error) {
      const message =
        error instanceof SkillsApiError ? error.message : editorLabels.error;

      toasterApi.error({
        title: editorLabels.errorTitle,
        description: message,
      });
    }
  };

  const handleRestore = async () => {
    try {
      await restoreSkill.mutateAsync(skillId);
      toasterApi.success({
        title: editorLabels.successTitle,
        description: editorLabels.restoredToast,
      });
    } catch {
      toasterApi.error({
        title: editorLabels.errorTitle,
        description: editorLabels.error,
      });
    }
  };

  return (
    <VStack h="full" w="full" gap={0} alignItems="stretch" minH="0">
      <Box
        p="4"
        borderBottomWidth="1px"
        borderColor="border.subtle"
        bg="bg.default"
      >
        <HStack justifyContent="space-between" alignItems="flex-start" gap={3}>
          <VStack alignItems="flex-start" gap={1} minW="0">
            <HStack gap={2} flexWrap="wrap">
              {skill ? (
                <>
                  <Badge variant={isCustomer ? 'outline' : 'fill'}>
                    {isCustomer ? listLabels.customer : listLabels.platform}
                  </Badge>
                  {skill.deletedAt ? (
                    <Badge variant="emphasis" colorScheme="red.9">
                      {listLabels.tombstoned}
                    </Badge>
                  ) : skill.publishedVersionId ? (
                    <Badge variant="outline" colorScheme="green.9">
                      {listLabels.published}
                    </Badge>
                  ) : (
                    <Badge variant="outline">{listLabels.draft}</Badge>
                  )}
                  {skill.draftVersion ? (
                    <Badge variant="outline">
                      {listLabels.draftBadgeLabel}
                    </Badge>
                  ) : null}
                </>
              ) : null}
            </HStack>
            <Text fontSize="xl" fontWeight="semibold" color="fg.default">
              {headerName || listLabels.title}
            </Text>
            {headerDescription ? (
              <Text fontSize="sm" color="fg.muted" lineHeight="1.6">
                {headerDescription}
              </Text>
            ) : null}
          </VStack>
          <HStack gap={1.5} flexShrink={0}>
            <Button
              variant="outline"
              size="xs"
              h="7"
              onClick={handleReturnToSkillsPage}
              title={listLabels.title}
            >
              <ArrowLeft02Icon size={12} />
              {listLabels.title}
            </Button>
            {isCustomer && skill ? (
              skill.deletedAt ? (
                <Button
                  variant="outline"
                  size="xs"
                  h="7"
                  onClick={handleRestore}
                  loading={restoreSkill.isPending}
                >
                  {editorLabels.restore}
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="xs"
                    h="7"
                    colorScheme="red.9"
                    onClick={handleDelete}
                    loading={deleteSkill.isPending}
                  >
                    {editorLabels.delete}
                  </Button>
                  <Button
                    variant="outline"
                    size="xs"
                    h="7"
                    onClick={handleOpenSkillEditor}
                  >
                    {editorLabels.edit}
                  </Button>
                  {skill.draftVersion ? (
                    <Button
                      size="xs"
                      h="7"
                      onClick={handlePublish}
                      loading={publishSkill.isPending}
                    >
                      {editorLabels.publish}
                    </Button>
                  ) : null}
                </>
              )
            ) : null}
          </HStack>
        </HStack>
      </Box>
      <Scroll w="full" h="full">
        <Box w="full" p="4">
          {isLoading || !skill ? (
            <HStack justifyContent="center" py={6}>
              <Spinner />
            </HStack>
          ) : (
            <VStack gap={6} alignItems="stretch">
              <Section title={detailLabels.identityTitle}>
                <Box
                  borderRadius="md"
                  borderWidth="1px"
                  borderStyle="solid"
                  borderColor="border.neutral.muted"
                  bg="bg.default"
                  p={3}
                >
                  <VStack alignItems="stretch" gap={2}>
                    <DetailRow
                      label={detailLabels.slugLabel}
                      value={skill.slug}
                      mono
                    />
                    <DetailRow
                      label={detailLabels.statusLabel}
                      value={
                        skill.deletedAt
                          ? listLabels.tombstoned
                          : skill.publishedVersionId
                            ? listLabels.published
                            : listLabels.draft
                      }
                    />
                  </VStack>
                </Box>
                {isCustomer ? (
                  <HStack
                    gap={2}
                    alignItems="flex-start"
                    borderRadius="md"
                    borderWidth="1px"
                    borderStyle="solid"
                    borderColor="border.neutral.muted"
                    bg="bg.neutral.subtleSoft"
                    p={3}
                  >
                    <Box color="fg.muted" mt={0.5}>
                      <SquareLock01Icon size={12} />
                    </Box>
                    <Text fontSize="xs" color="fg.muted" lineHeight="1.6">
                      {detailLabels.tenantScopeNote}
                    </Text>
                  </HStack>
                ) : null}
              </Section>

              <Section title={detailLabels.triggerTitle}>
                {headerDescription ? (
                  <Box
                    borderRadius="md"
                    borderLeftWidth="3px"
                    borderLeftColor="border.neutral.default"
                    bg="bg.default"
                    px={3}
                    py={2}
                  >
                    <Text fontSize="sm" color="fg.default" lineHeight="1.6">
                      {headerDescription}
                    </Text>
                  </Box>
                ) : (
                  <Text fontSize="sm" color="fg.muted">
                    {detailLabels.triggerEmpty}
                  </Text>
                )}
              </Section>

              <Section title={detailLabels.instructionsTitle}>
                <Box
                  borderRadius="md"
                  borderWidth="1px"
                  borderStyle="solid"
                  borderColor="border.neutral.muted"
                  bg="bg.default"
                  px={4}
                  py={3}
                >
                  {bodyMarkdown.trim().length === 0 ? (
                    <Text fontSize="sm" color="fg.muted">
                      {detailLabels.instructionsEmpty}
                    </Text>
                  ) : (
                    <MarkdownPreview source={bodyMarkdown} />
                  )}
                </Box>
              </Section>

              <Section title={versionLabels.title}>
                {versions.length === 0 ? (
                  <Text fontSize="sm" color="fg.muted">
                    {versionLabels.noHistory}
                  </Text>
                ) : (
                  <VStack gap={2} alignItems="stretch">
                    {versions.map((version) => {
                      const isCurrent =
                        version.versionId === skill.publishedVersionId;

                      return (
                        <HStack
                          key={version.versionId}
                          justifyContent="space-between"
                          p={2}
                          borderWidth="1px"
                          borderColor="border.subtle"
                          borderRadius="md"
                        >
                          <VStack alignItems="start" gap={0.5}>
                            <HStack gap={1.5} alignItems="center">
                              <Text fontSize="sm" fontWeight="medium">
                                {versionLabels.versionLabel(version.version)}
                              </Text>
                              {isCurrent ? (
                                <Badge variant="outline">
                                  {versionLabels.current}
                                </Badge>
                              ) : null}
                            </HStack>
                            <Text fontSize="xs" color="fg.muted">
                              {version.publishedAt
                                ? versionLabels.publishedAtLabel(
                                    formatDate(new Date(version.publishedAt)),
                                  )
                                : versionLabels.draft}
                            </Text>
                          </VStack>
                          {isCustomer && !isCurrent && version.publishedAt ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRollback(version.versionId)}
                              loading={rollbackSkill.isPending}
                            >
                              {versionLabels.rollbackTo}
                            </Button>
                          ) : null}
                        </HStack>
                      );
                    })}
                  </VStack>
                )}
              </Section>

              <Section title={auditLabels.title}>
                {entries.length === 0 ? (
                  <Text fontSize="sm" color="fg.muted">
                    {auditLabels.noActivity}
                  </Text>
                ) : (
                  <VStack gap={1} alignItems="stretch">
                    {entries.map((entry) => (
                      <HStack
                        key={entry.auditId}
                        justifyContent="space-between"
                        fontSize="sm"
                      >
                        <Text>
                          {auditLabels.actionLabel(entry.action)}{' '}
                          {entry.actorType === 'PLATFORM' ||
                          entry.actorType === 'system'
                            ? auditLabels.byPlatform
                            : entry.actorId
                              ? auditLabels.byUserLabel(entry.actorId)
                              : ''}
                        </Text>
                        <Text color="fg.muted">
                          {formatDate(new Date(entry.createdAt))}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                )}
              </Section>
            </VStack>
          )}
        </Box>
      </Scroll>
    </VStack>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <VStack alignItems="stretch" gap={2}>
      <Text
        fontSize="xs"
        fontWeight="semibold"
        color="fg.default"
        textTransform="uppercase"
        letterSpacing="wide"
      >
        {title}
      </Text>
      {children}
    </VStack>
  );
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <VStack alignItems="stretch" gap={0.5}>
      <Text
        fontSize="2xs"
        color="fg.muted"
        textTransform="uppercase"
        letterSpacing="wide"
      >
        {label}
      </Text>
      <Text
        fontSize="sm"
        color="fg.default"
        fontFamily={mono ? 'mono' : undefined}
        wordBreak="break-all"
      >
        {value}
      </Text>
    </VStack>
  );
}
